import classNames from "classnames";
import React, { type FC, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { TaskTreeNode } from "@app/tasks/types";
import { Svg } from "@shared/ui/svg";
import { getNodesAtLevel, isFolderNode, resolveNodeIcon } from "./taskTreeHelpers";
import "./TaskTilesNavigation.scss";

/**
 * Пропсы {@link TaskTilesNavigation}.
 */
export interface TaskTilesNavigationProps {
    /** Дерево задач (корень). */
    nodes: TaskTreeNode[];
    /**
     * `id` текущей открытой папки или `null` для корневого уровня.
     * @default null
     */
    currentFolderId: string | null;
    /**
     * Вызывается при тапе по папке (drill-down).
     * @param folderId `id` папки или `null` (не используется компонентом для «назад» —
     * подъём делает владелец через `App Back` / шапку)
     */
    onFolderChange: (folderId: string | null) => void;
    /**
     * Показывать иконки узла (`task.Icon` или известные папки), если они разрешились.
     * @default true
     */
    showIcons?: boolean;
}

function renderTileContent(node: TaskTreeNode, showIcons: boolean): ReactNode {
    const icon = showIcons ? resolveNodeIcon(node) : undefined;

    return (
        <>
            {icon != null && (
                <span className="task_tiles_navigation__tile-icon" aria-hidden="true">
                    <Svg src={icon} />
                </span>
            )}
            <span className="task_tiles_navigation__tile-name">{node.name}</span>
        </>
    );
}

/**
 * Плиточная навигация по дереву задач (drill-down). \
 * Controlled: текущий уровень задаёт `currentFolderId`.
 *
 * @example
 * ```tsx
 * <TaskTilesNavigation
 *   nodes={nodes}
 *   currentFolderId={folderId}
 *   onFolderChange={setFolderId}
 * />
 * ```
 */
export const TaskTilesNavigation: FC<TaskTilesNavigationProps> = ({
    nodes,
    currentFolderId,
    onFolderChange,
    showIcons = true,
}) => {
    const levelNodes = getNodesAtLevel(nodes, currentFolderId);

    if (nodes.length === 0) {
        return <div className="task_tiles_navigation__empty">Список задач пуст.</div>;
    }

    if (levelNodes.length === 0) {
        return <div className="task_tiles_navigation__empty">В этой папке нет задач.</div>;
    }

    return (
        <nav className="task_tiles_navigation" aria-label="Задачи">
            <ul className="task_tiles_navigation__grid">
                {levelNodes.map((node) => {
                    const isFolder = isFolderNode(node);
                    const content = renderTileContent(node, showIcons);

                    if (isFolder) {
                        return (
                            <li key={node.id} className="task_tiles_navigation__cell">
                                <button
                                    type="button"
                                    className="task_tiles_navigation__tile"
                                    disabled={node.disabled === true}
                                    onClick={() => {
                                        onFolderChange(node.id);
                                    }}
                                >
                                    {content}
                                </button>
                            </li>
                        );
                    }

                    const route = node.data.route;
                    const isDisabled = node.disabled === true || route == null || route === "";

                    if (isDisabled) {
                        return (
                            <li key={node.id} className="task_tiles_navigation__cell">
                                <div
                                    className={classNames(
                                        "task_tiles_navigation__tile",
                                        "task_tiles_navigation__tile--disabled",
                                    )}
                                >
                                    {content}
                                </div>
                            </li>
                        );
                    }

                    return (
                        <li key={node.id} className="task_tiles_navigation__cell">
                            <Link to={route} className="task_tiles_navigation__tile">
                                {content}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};
